import React, { useMemo } from 'react';
import dynamic from 'next/dynamic';

import useScreen from '@lyearn/core/hooks/useScreen';
import { cn } from '@lyearn/style';
import { Stack } from '@lyearn/ui';
import PageLayout from '@/components/PageLayout';
import { useRouteChromeOverrides } from '@/components/RouteChrome';
import { useSidebarCollapse } from '@/components/SidebarCollapse';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import { useAppConfig } from '@/hooks/useAppConfig';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { legacyAdminSidebarStyles } from '@/modules/corporate/modules/admin/components/LegacySidebar/style';
import {
  SIDEBAR_EXPANDED_WIDTH,
  SIDEBAR_RAIL_WIDTH,
} from '@/modules/corporate/modules/admin/components/Sidebar/style';
import type { SidebarItems } from '@/modules/corporate/modules/admin/hooks/useSidebar';
import useUnifiedNav from '@/modules/corporate/navigation/hooks/useUnifiedNav';
import { toSidebarItems } from '@/modules/corporate/navigation/utils/toSidebarItems';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';

const Content = dynamic(
  () => import('@/modules/corporate/modules/admin/components/Admin/Content'),
  {
    ssr: false,
  },
);
const LegacySidebar = dynamic(
  () => import('@/modules/corporate/modules/admin/components/LegacySidebar'),
  { ssr: false },
);
const Sidebar = dynamic(() => import('@/modules/corporate/modules/admin/components/Sidebar'), {
  ssr: false,
});

interface AppShellProps {
  children: React.ReactNode;
  showSidebarByDefault?: boolean;
  legacySidebarItems?: SidebarItems;
}

interface ShellProps {
  children: React.ReactNode;
  showSidebar: boolean;
}

function LegacyAdminShell(props: ShellProps & { sidebarItems: SidebarItems }) {
  const { children, showSidebar, sidebarItems } = props;
  const { [FeatureFlags.ShowUserProfile]: showUserProfile } = useFeatureFlags();

  return (
    <Stack className="flex-1">
      {showSidebar ? (
        <LegacySidebar
          className={cn('border-divider border-e', legacyAdminSidebarStyles, {
            'pt-50': !showUserProfile,
          })}
          showUserProfile={showUserProfile}
          sidebarItems={sidebarItems}
        />
      ) : null}
      <Content>{children}</Content>
    </Stack>
  );
}

function UnifiedShell(props: ShellProps) {
  const { children, showSidebar } = props;
  const isAdminRoute = useAdminRoute();
  const { nav, isLoading: isNavLoading } = useUnifiedNav();
  const { isCollapsed } = useSidebarCollapse();
  const { isMobile } = useScreen();
  const { [FeatureFlags.ShowUserProfile]: showUserProfile } = useFeatureFlags();

  const sidebarItems = useMemo(() => toSidebarItems(nav), [nav]);

  const pageContent = <PageLayout>{children}</PageLayout>;
  const content = isAdminRoute ? <Content>{pageContent}</Content> : pageContent;

  if (!showSidebar || isMobile) {
    return <>{content}</>;
  }

  return (
    <Stack className="flex-1">
      <aside
        className="sticky top-0 z-50 h-screen flex-shrink-0 self-start transition-[width] duration-300 ease-in-out"
        style={{ width: isCollapsed ? SIDEBAR_RAIL_WIDTH : SIDEBAR_EXPANDED_WIDTH }}>
        <Sidebar
          logoOnlyHeader
          collapsed={isCollapsed}
          loading={isNavLoading}
          showUserProfile={showUserProfile}
          sidebarItems={sidebarItems}
        />
      </aside>
      <Stack className="min-w-0 flex-1" direction="col">
        {content}
      </Stack>
    </Stack>
  );
}

function AppShell(props: AppShellProps) {
  const { children, legacySidebarItems, showSidebarByDefault = true } = props;
  const {
    ui: { isUnifiedSidebar },
  } = useAppConfig();
  const { sidebar: sidebarOverride } = useRouteChromeOverrides();
  const user = useUserContext();

  const showSidebar = sidebarOverride ?? showSidebarByDefault;

  if (!isUnifiedSidebar) {
    return legacySidebarItems ? (
      <LegacyAdminShell showSidebar={showSidebar} sidebarItems={legacySidebarItems}>
        {children}
      </LegacyAdminShell>
    ) : (
      <>{children}</>
    );
  }

  if (!user.id) {
    return <>{children}</>;
  }

  return <UnifiedShell showSidebar={showSidebar}>{children}</UnifiedShell>;
}

export default AppShell;
